import { Icon, WARP_ME_EXTERNAL_LINKS } from "@warpme/ui"
import type { FC } from "react"
import { useMemo, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { buildConsumerRestaurantUrl } from "@/lib/consumer-links"
import { auth } from "@/lib/firebase"
import { useTenantAccess } from "@/lib/tenant-access"
import { useTenantShop } from "@/lib/use-tenant-shop"
import { useCurrentUser } from "@/lib/useCurrentUser"
import { cn } from "@/lib/utils"

const MENU_ITEMS_PRIMARY = [
  { to: "/dashboard", label: "ダッシュボード", requiresManager: false },
  { to: "/tickets", label: "チケット", requiresManager: true },
  { to: "/sales", label: "売上", requiresManager: true },
  { to: "/setting/shop", label: "店舗設定", requiresManager: true },
] as const

const SUPPORT_LINKS = [
  { href: WARP_ME_EXTERNAL_LINKS.support.tenantFaq, label: "よくあるご質問" },
  { href: WARP_ME_EXTERNAL_LINKS.support.company, label: "運営会社について" },
  { href: WARP_ME_EXTERNAL_LINKS.support.contact, label: "お問い合わせ" },
] as const

const OTHER_LINKS = [
  { href: WARP_ME_EXTERNAL_LINKS.legal.tenantTerms, label: "利用規約" },
  { href: WARP_ME_EXTERNAL_LINKS.legal.privacyPolicy, label: "プライバシーポリシー" },
  {
    href: WARP_ME_EXTERNAL_LINKS.legal.specifiedCommercialTransactions,
    label: "特定商取引法に基づく表記",
  },
] as const

type TenantMenuProps = {
  triggerClassName?: string
  iconClassName?: string
}

const roleLabel = (role: string | null | undefined) => {
  if (role === "OWNER") return "オーナー"
  if (role === "MANAGER") return "マネージャー"
  if (role === "STAFF") return "スタッフ"
  return null
}

const isMenuItemActive = (pathname: string, to: string) => {
  if (to === "/dashboard") return pathname.startsWith("/dashboard")
  if (to === "/tickets") return pathname.startsWith("/tickets")
  if (to === "/sales") return pathname.startsWith("/sales")
  if (to === "/setting/shop") return pathname.startsWith("/setting")
  return pathname === to
}

export const TenantMenu: FC<TenantMenuProps> = ({ triggerClassName, iconClassName }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const currentUser = useCurrentUser()
  const { canManageTenant, role } = useTenantAccess()
  const { shopSettings } = useTenantShop()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)

  const shopUrl = useMemo(
    () =>
      shopSettings?.restaurantId
        ? buildConsumerRestaurantUrl(
            import.meta.env.VITE_CONSUMER_BASE_URL,
            shopSettings.restaurantId
          )
        : null,
    [shopSettings?.restaurantId]
  )
  const primaryMenuItems = MENU_ITEMS_PRIMARY.filter(
    (item) => !item.requiresManager || canManageTenant
  )
  const userRoleLabel = roleLabel(role)

  return (
    <>
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn("rounded-full border border-transparent", triggerClassName)}
            aria-label="メニュー"
          >
            <Icon name="menu" className={cn("h-5 w-5", iconClassName)} />
          </Button>
        </DrawerTrigger>
        <DrawerContent
          side="right"
          className="top-0 h-screen w-[315px] max-w-none rounded-none border-0 bg-bg-white p-0 shadow-right [&>button]:hidden"
        >
          <DrawerTitle className="sr-only">メニュー</DrawerTitle>

          <div className="flex h-12 items-center justify-between px-2">
            {shopUrl ? (
              <a
                href={shopUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-7 items-center gap-0.5 px-2 text-label-lg font-semibold leading-[1.35] text-text transition-colors hover:bg-background"
              >
                店舗ページをみる
                <Icon name="open" className="size-5" />
              </a>
            ) : (
              <div className="h-7" />
            )}
            <DrawerClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-10 rounded-full bg-highlight text-text backdrop-blur-[10px] hover:bg-background"
                aria-label="メニュー"
              >
                <Icon name="close" className="size-6" />
              </Button>
            </DrawerClose>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto">
              <section className="flex flex-col">
                <h3 className="bg-background px-4 pb-2 pt-5 text-label-lg font-light leading-[1.35] text-text">
                  チケットと店舗
                </h3>
                <nav className="flex flex-col border-b border-border" aria-label="チケットと店舗">
                  {primaryMenuItems.map((item) => {
                    const active = isMenuItemActive(location.pathname, item.to)

                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        aria-current={active ? "page" : undefined}
                        onClick={() => setDrawerOpen(false)}
                        className="flex h-11 items-center border-b border-border px-4 text-body-md font-semibold leading-[1.5] text-text transition-colors last:border-b-0 hover:bg-background"
                      >
                        {item.label}
                      </Link>
                    )
                  })}
                </nav>
              </section>

              <section className="flex flex-col">
                <h3 className="bg-background px-4 pb-2 pt-5 text-label-lg font-light leading-[1.35] text-text">
                  サポート
                </h3>
                <div className="flex flex-col border-b border-border">
                  {SUPPORT_LINKS.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex h-11 items-center border-b border-border px-4 text-body-md font-semibold leading-[1.5] text-text transition-colors last:border-b-0 hover:bg-background"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </section>

              <section className="flex flex-col">
                <h3 className="bg-background px-4 pb-2 pt-5 text-label-lg font-light leading-[1.35] text-text">
                  その他
                </h3>
                <div className="flex flex-col border-b border-border">
                  {OTHER_LINKS.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex min-h-9 items-center border-b border-border px-4 py-2 text-label-lg font-light leading-[1.35] text-text transition-colors last:border-b-0 hover:bg-background"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </section>
            </div>

            <div className="border-t border-border px-4 pb-5 pt-3">
              <div className="flex items-center gap-3">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-full border border-border bg-bg-white text-text">
                  <Icon name="account" className="size-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body-sm font-semibold leading-[1.5] text-text">
                    {currentUser?.displayName ?? "-"}
                  </p>
                  <p className="truncate text-label-sm font-light leading-[1.35] text-gray">
                    {currentUser?.email ?? "-"}
                  </p>
                  {userRoleLabel ? (
                    <p className="truncate text-label-lg font-light leading-[1.35] text-text">
                      {userRoleLabel}
                    </p>
                  ) : null}
                </div>
              </div>

              <Button
                type="button"
                variant="neutral"
                className="mt-3 h-11 w-full rounded-lg text-body-md font-semibold"
                onClick={() => setLogoutDialogOpen(true)}
              >
                ログアウト
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent className="w-[288px] rounded-xl p-4 sm:max-w-[288px]">
          <DialogTitle className="text-center text-[15px]">ログアウトしますか？</DialogTitle>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant="secondary"
              className="h-11 rounded-lg bg-[#f5f5f5] text-black"
              onClick={() => setLogoutDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-lg border-[#f34a0c] text-[#f34a0c]"
              onClick={async () => {
                await auth.signOut()
                setLogoutDialogOpen(false)
                navigate("/login")
              }}
            >
              ログアウトする
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
